import { LearnMoreLink, Page } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { useModal } from '../../../hooks/use-modal';
import { PriceListEditForm } from '../components/builder/price-list-edit-form';
import { ClearPriceListModal } from '../components/modals/clear-price-list-modal';
import { DeletePriceListModal } from '../components/modals/delete-price-list-modal';
import { useExportPriceListCsv } from '../hooks/use-export-price-list-csv';
import { usePriceList } from '../hooks/use-price-list';
import type { PriceListApiResponse } from '../types';

export const PriceListEditPage = withPageProviders(PriceListEditPageContent);

function PriceListEditPageContent() {
  const { navigationHandler } = useHandlers();
  const { slug } = navigationHandler.params();
  const exportCsv = useExportPriceListCsv();

  const { data: priceList } = usePriceList(slug ?? '');

  const [listToDelete, setListToDelete] = useState<PriceListApiResponse | null>(null);
  const {
    isOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal();

  const [listToClear, setListToClear] = useState<PriceListApiResponse | null>(null);
  const {
    isOpen: isClearModalOpen,
    openModal: openClearModal,
    closeModal: closeClearModal,
  } = useModal();

  if (!slug) {
    navigationHandler.navigate('/pricing/lists');
    return null;
  }

  const secondaryActions = priceList
    ? [
        {
          onClick: () => {
            void exportCsv(priceList.slug);
          },
          label: t('buttons:download_csv'),
          icon: 'download' as const,
          disabled: (priceList.num_manual_prices ?? 0) === 0,
        },
        {
          onClick: () => {
            setListToClear(priceList);
            openClearModal();
          },
          label: t('buttons:clear'),
          icon: 'minusCircle' as const,
        },
        {
          onClick: () => {
            setListToDelete(priceList);
            openDeleteModal();
          },
          label: t('buttons:delete'),
          icon: 'delete' as const,
          danger: true,
        },
      ]
    : undefined;

  return (
    <Page
      narrow
      title={priceList?.name || t('price_list_builder.header.title.edit')}
      secondaryActions={secondaryActions}
      description={
        <>
          {t('price_list_builder.header.description.update')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/managing-pricing" />
        </>
      }
      backUrl="/pricing/lists"
    >
      <PriceListEditForm
        slug={slug}
        onSuccess={() => navigationHandler.navigate('/pricing/lists')}
        onCancel={() => navigationHandler.navigate('/pricing/lists')}
      />

      <DeletePriceListModal
        priceList={listToDelete}
        isOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
        onDeleted={() => navigationHandler.navigate('/pricing/lists')}
      />

      <ClearPriceListModal
        priceList={listToClear}
        isOpen={isClearModalOpen}
        closeModal={closeClearModal}
      />
    </Page>
  );
}
